import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import * as moment from 'moment';
import * as _ from 'lodash';
import { interval, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorMessage } from 'src/app/common/messages';
import { State } from 'src/app/dashboards/state';
import { register } from 'src/app/dashboards/widget/widget-helpers/helpers';
import { gridItem } from 'src/app/models';
import { CommonService } from 'src/app/services/services/common.service';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { EventQueueService } from 'src/app/services/services/EventQueueService';
import * as mainActions from 'src/app/state/app.actions';
import { WidgetBaseComponent } from '../../widget-base.component';

@Component({
    selector: 'app-repQDetails',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    standalone: false
})
@register("Reports")
export class ReportsComponent implements OnInit, OnDestroy, AfterViewInit, WidgetBaseComponent {

  @Input() public stepper;
  @Input() public isInWizard: boolean;
  @Input() public isStandAlone: boolean;
  @Input() public reportCode: string;
  @Input() public reportTitle: string;
  @Input() public downloadDialogNotifier: Subject<any>;
  @Input() public generateNotifier: Subject<any>;
  @Input() public orderColumnsNotifier: Subject<any>;
  @Output() public restrictedDataEvent = new EventEmitter<boolean>();
  @Output() public dataReceivedEvent = new EventEmitter<boolean>();

  _dataEquality: boolean;
  get dataEquality(): boolean {
    return this._dataEquality;
  }
  @Input() set dataEquality(value: boolean) {
    this._dataEquality = value;
    if (!value && !this.isStandAlone && this.stepper) {
      this.dataLoaded = false;
      this.getData();
    }
  }

  _gridItem: gridItem;
  get gridItem(): gridItem {
    return this._gridItem;
  }
  @Input() set gridItem(value: any) {
    this._gridItem = value;
    setTimeout(() => {
      this.notifier.next(false);
    }, 0);
  }

  restrictedData: boolean = false;
  dataLoaded: boolean = false;
  widgetType: number = 0;

  queues$: Observable<any[]>;
  terminate$ = new Subject<void>();
  data: any[];
  columns: any[];
  reportInfo: string;
  totals: any[];
  specialReportProperties: any;
  showTotal: boolean = true;
  tooManyRecords: boolean = false;

  private clearDashboardRequested: boolean = false;
  public notifier = new Subject<boolean>();

  constructor(private DashboardsService: DashboardService, private store: Store<State>, private i18nextPipe: I18NextPipe,
    private eventQueue: EventQueueService, private commonService: CommonService) { }
  ngAfterViewInit(): void {
    if (this.gridItem.setup.reportId) {
      this.getData();

      let period = this.gridItem.setup?.period??{};
      let toDate = this.commonService.calculateRelativeDate("to", this.gridItem);
      let toTimeInMinutes = this.commonService.getRelativeDateTimeInMinutes("to", this.gridItem);
      toDate.add(toTimeInMinutes,"minutes");
      if (!this.isInWizard && period.type == 1 && toDate.isAfter(moment()) ) {
          interval(15 * 60 * 1000).pipe(takeUntil(this.terminate$)).subscribe(_ => { this.getData(); });
      }
    }
  }

  ngOnInit(): void {
    if (this.isStandAlone) {
      this.generateNotifier?.pipe(takeUntil(this.terminate$)).subscribe(value => {
        setTimeout(() => {
          this.dataLoaded = false;
          this.getData();
        }, 0)
      });

      this.orderColumnsNotifier?.pipe(takeUntil(this.terminate$)).subscribe(value => {
        this.notifier.next(false);
      });
    }
  }

  ngOnDestroy(): void {
    this.terminate$.next()
    this.terminate$.complete()
  }
  getData(): void {
    this.reportInfo = "";
    this.gridItem.setup.reportType = this.gridItem.type ?? 4;
    this.tooManyRecords = false;
    let setup = _.cloneDeep(this.gridItem.setup);
    for (let i = 0; i < setup["G"]?.length; i++) {
      let index = setup["G"][i].toString().indexOf("_");
      if (index > -1) {
        setup["G"][i] = setup["G"][i].toString().substring(0, index);
      }
    }
    if (this.gridItem.setup["G"]) {
      setup["G"] = setup["G"].filter(g => g.toString().indexOf("_") < 0).map(g => Number(g)).filter((value, index, array)  => {
        return array.indexOf(value) === index
      });
    }
    
    this.DashboardsService.GetReportResult(setup).subscribe(
      (reportResult) => {
        if (reportResult === "Unauthorized") {
          this.restrictedData = true;
        } else {
          if (reportResult.reportInfo?.hideReport){
            this.store.dispatch(mainActions.addMessage({ message: new ErrorMessage("Sorry, too many records.", "Please narrow the selected time period and try again.") }));
            this.tooManyRecords = true;
          }
          this.data = reportResult.data;
          this.columns = reportResult.columns;
          this.totals = reportResult.totals;
          this.showTotal = reportResult.showTotal;
          this.reportInfo = this.commonService.reportInfoLine(this.gridItem, reportResult.reportInfo.lastReportTime, this.gridItem.setup.serviceNumber ?? "");
          this.specialReportProperties = reportResult.specialReportProp;
          this.emitDataReceived();
        }
      },
      (error: HttpErrorResponse) => {
        if (error.status == 401) {
          this.restrictedData = true;
        } else {
          this.handleError(error);
        }
      },
      () => {
        this.dataLoaded = true;
        this.widgetType = this.gridItem.type;
        if (this.columns) {
          setTimeout(() => {
            this.notifier.next(false);
          }, 0)
        }
      }
    );
  }

  emitDataReceived() {
    let isData = false;
    if (this.data.length > 0) isData = true;
    this.dataReceivedEvent.emit(isData);
  }

  handleError(error: any) {
    this.dataLoaded = true;
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}; ${error.error}`;
    }
    this.store.dispatch(mainActions.addMessage({ message: new ErrorMessage("Error", errorMessage) }));
  }

  displayWidget(): void { }
  onResized(event): void { }
}
